import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, Copy, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useChartTabs } from "@/lib/chartTabs";
import { patientOf } from "@/lib/claimReadiness";
import { useCw } from "./cwContext";
import { Seg, NoteBody, RxTab, InstructionsTab, MessagesTab, TranscriptTab, countIn, rxText, piText, msgText, parseTx } from "./enParts";

// Given a hint (a code rationale / blocker description) find the longest fragment that
// actually appears in the note. Sentences first, then shrinking word windows; else "".
function findPhrase(body, hint) {
  if (!hint) return "";
  const low = body.toLowerCase();
  const sents = String(hint).split(/[.;\n]+/).map((s) => s.trim()).filter((s) => s.length >= 8).sort((a, b) => b.length - a.length);
  for (const s of sents) if (low.includes(s.toLowerCase())) return s;
  const words = String(hint).split(/\s+/).filter(Boolean);
  for (const size of [8, 6, 5, 4]) {
    for (let i = 0; i + size <= words.length; i++) {
      const w = words.slice(i, i + size).join(" ");
      if (low.includes(w.toLowerCase())) return w;
    }
  }
  return "";
}

function buildBody(soap) {
  const order = [["Chief complaint", "chief_complaint"], ["Subjective", "subjective"], ["Objective", "objective"], ["Assessment", "assessment"], ["Plan", "plan"]];
  return order.filter(([, k]) => soap[k]).map(([lab, k]) => `${lab}:\n${soap[k]}`).join("\n\n");
}

const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return null; } };
const fmtStamp = (d) => d.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const TABS = [["note", "Note"], ["rx", "Rx"], ["pi", "Instructions"], ["msg", "Messages"], ["tx", "Transcript"]];
const TAB_NAME = Object.fromEntries(TABS);

export default function EncounterNote({ note, onOpenInChart, jump, onEditing }) {
  const cw = useCw();
  const { user } = useAuth();
  const enc = note?.encounter || {};
  const soap = enc.soap_note || {};
  const custom = String(soap.formatted_note || enc.formatted_note || "").trim();
  const standard = String(enc.soap_text || buildBody(soap) || "").trim();
  const hasCustom = !!custom && custom !== standard;
  const rx = Array.isArray(enc.prescriptions) ? enc.prescriptions : [];
  const rp = Array.isArray(enc.represcribe_meds) ? enc.represcribe_meds : [];
  const pi = enc.patient_instructions || null;
  const piSections = Array.isArray(pi?.sections) ? pi.sections : [];
  const guidance = Array.isArray(pi?.new_drug_guidance) ? pi.new_drug_guidance : [];
  const comms = enc.patient_comms || null;
  const messages = Array.isArray(comms?.messages) ? comms.messages : [];
  const txRows = useMemo(() => parseTx(String(enc.transcript || "").split(/\n+/).filter((l) => l.trim())), [enc.transcript]);

  const [tab, setTab] = useState("note");
  const [view, setView] = useState("custom");
  const [q, setQ] = useState("");
  const [mark, setMark] = useState("");
  const [piLang, setPiLang] = useState("en");
  const [msgLang, setMsgLang] = useState("en");
  const [lock, setLock] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [discardAsk, setDiscardAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stamp, setStamp] = useState(null);
  const taRef = useRef(null);
  const tabRefs = useRef({});

  const curView = hasCustom ? view : "standard";
  const noteBody = curView === "custom" ? custom : (standard || custom);
  useEffect(() => { onEditing?.(editing); }, [editing]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setEditing(false); setLock(false); setStamp(null); setQ(""); setMark(""); }, [note?.id]);

  // A "Why?" / blocker jump from the claim side: open Note, mark the phrase, scroll to it.
  useEffect(() => {
    if (!jump || !jump.nonce || editing) return;
    setQ(""); setTab("note");
    setMark(findPhrase(noteBody, jump.text));
    const t = setTimeout(() => document.querySelector(".cfv-en-panel mark")?.scrollIntoView({ block: "center", behavior: "smooth" }), 60);
    return () => clearTimeout(t);
  }, [jump?.nonce]); // eslint-disable-line react-hooks/exhaustive-deps

  const term = q.length >= 2 ? q : mark;
  const texts = { note: noteBody, rx: rxText([...rx, ...rp]), pi: piText(piSections, guidance, piLang), msg: msgText(messages, msgLang), tx: txRows.map((r) => `${r.who} ${r.text}`).join("\n") };
  const hits = q.length >= 2 ? Object.fromEntries(TABS.map(([k]) => [k, countIn(texts[k], q)])) : null;
  const baseCount = { rx: rx.length || null, msg: messages.length || null };
  const otherHit = hits && !hits[tab] ? TABS.find(([k]) => hits[k]) : null;

  const onTabKey = (e) => {
    const i = TABS.findIndex(([k]) => k === tab);
    const n = e.key === "ArrowRight" ? i + 1 : e.key === "ArrowLeft" ? i - 1 : e.key === "Home" ? 0 : e.key === "End" ? TABS.length - 1 : null;
    if (n == null) return;
    e.preventDefault();
    const k = TABS[(n + TABS.length) % TABS.length][0];
    setTab(k); tabRefs.current[k]?.focus();
  };

  const { openNote } = useChartTabs();
  const recoding = !!cw?.recoding;
  const openEditor = () => {
    setLock(false); setDraft(noteBody); setEditing(true); setDiscardAsk(false);
    setTimeout(() => { const t = taRef.current; if (t) { t.focus(); t.setSelectionRange(0, 0); t.scrollTop = 0; } }, 30);
  };
  const toSharedEditor = () => { if (!openNote) return false; openNote({ noteId: note.id, name: patientOf(note) || "Patient", chartUuid: note.chart_uuid || null, fromLabel: "Claim" }); return true; };
  const onEdit = () => { if (note?.signed) { setLock(true); return; } if (!toSharedEditor()) openEditor(); };
  const unsignAndEdit = async () => {
    setBusy(true);
    try { await cw.unsign(); toast.success("Visit unsigned · sign it again when you're done"); if (!toSharedEditor()) openEditor(); }
    catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Couldn't unsign this visit."); }
    finally { setBusy(false); }
  };
  const changed = editing && draft !== noteBody;
  const save = async () => {
    if (!changed || busy) return;
    const body = !hasCustom ? { formatted_note: draft, soap_text: draft } : curView === "custom" ? { formatted_note: draft } : { soap_text: draft };
    setBusy(true);
    try {
      await cw.saveNote(body);
      setEditing(false); document.querySelector('[data-testid="cfv-rail"]')?.scrollTo({ top: 0 }); setStamp({ name: [user?.first_name, user?.last_name].filter(Boolean).join(" ") || user?.email || "you", at: fmtStamp(new Date()), view: curView });
      toast.success("Note saved · codes are out of date");
    } catch (e) { toast.error(e?.message || "Couldn't save the note."); }
    finally { setBusy(false); }
  };
  const cancel = () => { if (changed) setDiscardAsk(true); else setEditing(false); };
  const onTaKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") { e.preventDefault(); save(); } };
  const copyNote = () => { try { navigator.clipboard.writeText(noteBody); toast.success("Note copied"); } catch { /* clipboard unavailable */ } };

  const footMsg = !hasCustom ? "Saves the note. Codes will need a recode."
    : curView === "custom" ? "Saves the Custom note. Standard SOAP isn't changed. Codes will need a recode."
    : "Saves the Standard SOAP. The Custom note isn't changed. Codes will need a recode.";
  const failed = cw?.recodeErr != null;
  const showStale = !editing && (note?.codes_stale || recoding || failed);

  return (
    <div className={`cfv-en ${editing ? "editing" : ""}`} data-testid="cfv-encounter-note">
      <div className="cfv-en-top">
        <span>From a dictation session{note?.coded_at ? ` · coded ${fmtTime(note.coded_at)}` : enc.generated_at ? ` · generated ${fmtTime(enc.generated_at)}` : ""}</span>
        {onOpenInChart ? <button className="cfv-en-open" data-testid="cfv-en-open-chart" onClick={onOpenInChart}>Open in chart →</button> : null}
      </div>

      <div className={`cfv-en-find ${editing ? "dis" : ""}`}>
        <Search size={14} />
        <input value={q} disabled={editing} onChange={(e) => { setQ(e.target.value); setMark(""); }} placeholder="Find in this visit" data-testid="cfv-en-find" aria-label="Find in this visit" />
        {q ? <button type="button" aria-label="Clear search" onClick={() => setQ("")} data-testid="cfv-en-find-clear"><X size={13} /></button> : null}
      </div>

      <div className="cfv-en-tabs" role="tablist" aria-label="Encounter note" onKeyDown={onTabKey} data-testid="cfv-en-tabs">
        {TABS.map(([k, label]) => {
          const on = tab === k, dis = editing && k !== "note";
          const n = hits ? hits[k] : baseCount[k];
          return (
            <button key={k} ref={(el) => { tabRefs.current[k] = el; }} role="tab" type="button" aria-selected={on} tabIndex={on ? 0 : -1} disabled={dis}
              className={`${on ? "on" : ""} ${hits && !hits[k] ? "nohit" : ""} ${hits && hits[k] ? "hit" : ""}`} onClick={() => setTab(k)} data-testid={`cfv-en-tab-${k}`}>
              {label}{n ? <span className="c">{n}</span> : null}
            </button>
          );
        })}
      </div>

      <div className="cfv-en-panel" role="tabpanel" data-testid={`cfv-en-panel-${tab}`}>
        {otherHit ? <div className="cfv-en-grey nomatch" data-testid="cfv-en-nomatch">No matches here · <button type="button" onClick={() => setTab(otherHit[0])}>{hits[otherHit[0]]} in {TAB_NAME[otherHit[0]]}</button>.</div> : null}

        {tab === "note" ? (
          <>
            <div className="cfv-en-row">
              {hasCustom ? <Seg value={view} onChange={setView} disabled={editing} options={[["custom", "Custom note"], ["standard", "Standard SOAP"]]} testid="cfv-en-view" /> : <span className="cfv-en-grey" data-testid="cfv-en-view-label">Standard SOAP</span>}
              <span className="cfv-en-acts">
                <button type="button" className="cfv-en-sbtn" onClick={copyNote} data-testid="cfv-en-copy"><Copy size={12} /> Copy</button>
                {cw && !editing ? <button type="button" className="cfv-en-sbtn" onClick={onEdit} disabled={recoding || busy} data-testid="cfv-en-edit"><Pencil size={12} /> Edit</button> : null}
              </span>
            </div>
            {stamp && stamp.view === curView && !editing ? <div className="cfv-en-grey sm" data-testid="cfv-en-stamp">Edited by {stamp.name} · {stamp.at}{cw?.openChangeLog ? <> · <button type="button" className="cfv-en-link" onClick={() => cw.openChangeLog("note")} data-testid="cfv-en-view-changes">View changes</button></> : null}</div> : null}

            {lock && !editing ? (
              <div className="cfv-en-block" data-testid="cfv-en-lock">
                <b>Signed notes are locked</b>
                <p className="cfv-en-grey">Unsign to edit, then sign again. Claim saves are blocked until the visit is signed.</p>
                <div className="cfv-en-btns">
                  <button type="button" className="cfv-en-sbtn" onClick={unsignAndEdit} disabled={busy} data-testid="cfv-en-unsign-edit">{busy ? "Unsigning…" : "Unsign and edit"}</button>
                  <button type="button" className="cfv-en-qbtn" onClick={() => setLock(false)} data-testid="cfv-en-lock-cancel">Cancel</button>
                </div>
              </div>
            ) : null}

            {showStale ? (
              <div className="cfv-en-block amber" data-testid="cfv-en-stale">
                {recoding ? (
                  <><b className="ink">Recoding from the note…</b><div className="cfv-en-bar"><span /></div></>
                ) : failed ? (
                  <><b><span className="dot" />Recode didn't finish</b><p className="cfv-en-grey">{cw.recodeErr || "The recode stopped before it finished."}</p>
                    <div className="cfv-en-btns"><button type="button" className="cfv-en-sbtn" onClick={cw.doRecode} data-testid="cfv-en-recode-retry">Try again</button></div></>
                ) : (
                  <><b><span className="dot" />Codes out of date</b><p className="cfv-en-grey">The note changed after it was coded. Recode to update the diagnoses and service lines.</p>
                    {cw ? <div className="cfv-en-btns"><button type="button" className="cfv-en-sbtn" onClick={cw.doRecode} data-testid="cfv-en-recode">Recode from note</button></div> : null}</>
                )}
              </div>
            ) : null}

            {editing ? (
              <div className="cfv-en-edit" data-testid="cfv-en-editor">
                <textarea ref={taRef} value={draft} onChange={(e) => { setDraft(e.target.value); setDiscardAsk(false); }} onKeyDown={onTaKey} aria-label="Edit note" data-testid="cfv-en-textarea" />
                <div className="cfv-en-efoot">
                  {discardAsk ? (
                    <>
                      <span className="msg ink">Discard your changes?</span>
                      <button type="button" className="cfv-en-qbtn" onClick={() => setDiscardAsk(false)} data-testid="cfv-en-keep-editing">Keep editing</button>
                      <button type="button" className="cfv-en-sbtn" onClick={() => { setDiscardAsk(false); setEditing(false); }} data-testid="cfv-en-discard">Discard</button>
                    </>
                  ) : (
                    <>
                      <span className="msg">{footMsg}</span>
                      <button type="button" className="cfv-en-qbtn" onClick={cancel} data-testid="cfv-en-cancel">Cancel</button>
                      <button type="button" className="cfv-en-pbtn" onClick={save} disabled={!changed || busy} data-testid="cfv-en-save">{busy ? "Saving…" : "Save note"}</button>
                    </>
                  )}
                </div>
              </div>
            ) : noteBody ? <NoteBody text={noteBody} q={term} /> : <div className="cfv-en-grey">No note text on this visit.</div>}
          </>
        ) : tab === "rx" ? (rx.length || rp.length ? <RxTab rx={rx} rp={rp} q={term} /> : <div className="cfv-en-grey">No prescriptions on this visit.</div>)
          : tab === "pi" ? (piSections.length || guidance.length ? <InstructionsTab sections={piSections} guidance={guidance} lang={piLang} setLang={setPiLang} q={term} /> : <div className="cfv-en-grey">No patient instructions on this visit.</div>)
          : tab === "msg" ? (messages.length ? <MessagesTab messages={messages} anchor={comms?.anchor_date} lang={msgLang} setLang={setMsgLang} q={term} /> : <div className="cfv-en-grey">No patient messages planned.</div>)
          : (txRows.length ? <TranscriptTab rows={txRows} q={term} /> : <div className="cfv-en-grey">No transcript on this visit.</div>)}
      </div>
    </div>
  );
}
